import { createContext, useContext, useMemo, useState } from 'react'

import useI18n from '@/shared/hooks/usei18n'

import {
  formatNetMassFromTonnes,
  NetMassDisplayUnit
} from './netMassDisplayUnit'

type NetMassDisplayUnitContextValue = {
  unit: NetMassDisplayUnit
  setUnit: (unit: NetMassDisplayUnit) => void
  formatNetMass: (tonnes: number) => string
  netMassColumnLabel: string
}

const NetMassDisplayUnitContext = createContext<NetMassDisplayUnitContextValue | null>(null)

export function NetMassDisplayUnitProvider({ children }: { children: React.ReactNode }) {
  const [unit, setUnit] = useState<NetMassDisplayUnit>('tonnes')
  const { t, numberFormatter3Decimals } = useI18n()

  const value = useMemo((): NetMassDisplayUnitContextValue => {
    const formatNetMass = (tonnes: number) => {
      if (tonnes <= 0) {
        return '–'
      }
      return formatNetMassFromTonnes(tonnes, unit, numberFormatter3Decimals)
    }

    const netMassColumnLabel =
      unit === 'tonnes'
        ? t('common:netMassDisplay.column.netMassTonnes')
        : t('common:netMassDisplay.column.netMassKilograms')

    return {
      unit,
      setUnit,
      formatNetMass,
      netMassColumnLabel
    }
  }, [unit, numberFormatter3Decimals, t])

  return (
    <NetMassDisplayUnitContext.Provider value={value}>{children}</NetMassDisplayUnitContext.Provider>
  )
}

export function useNetMassDisplay() {
  const context = useContext(NetMassDisplayUnitContext)

  if (!context) {
    throw new Error('useNetMassDisplay must be used within a NetMassDisplayUnitProvider')
  }

  return context
}
